import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Eye, Loader2, Plus, Save, Trash2, Upload, RotateCcw } from 'lucide-react';
import { fetchBillingSettings } from './billingApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_LOGO_HINT = 'Ahora se usa el logo de KeyPubli de tu factura de ejemplo.';

const MethodRow = ({ m, i, update, remove }) => (
  <div className="grid grid-cols-1 md:grid-cols-[180px_1fr_auto_auto] gap-2 items-start border rounded-lg p-2" data-testid={`billing-method-${i}`}>
    <Input value={m.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="Nombre (p. ej. PayPal)" data-testid={`billing-method-name-${i}`} />
    <Textarea rows={2} value={m.text} onChange={(e) => update(i, { text: e.target.value })}
      placeholder="Texto en la factura. Pon **entre dos asteriscos** lo que va en negrita" data-testid={`billing-method-text-${i}`} />
    <label className="flex items-center gap-1.5 text-xs pt-2 whitespace-nowrap">
      <Checkbox checked={!!m.default} onCheckedChange={(v) => update(i, { default: !!v })} data-testid={`billing-method-default-${i}`} />
      Marcado por defecto
    </label>
    <Button variant="ghost" size="sm" onClick={() => remove(i)} className="text-rose-600" data-testid={`billing-method-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
  </div>
);

export const BillingSettingsTab = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setS(await fetchBillingSettings()); } catch { toast.error('Error al cargar los datos de facturación'); }
  };
  useEffect(() => { load(); }, []);
  if (!s) return <div className="p-6"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const updateMethod = (i, patch) => set('payment_methods', s.payment_methods.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  const removeMethod = (i) => set('payment_methods', s.payment_methods.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        company_name: s.company_name, nif: s.nif, address: s.address, invoice_prefix: s.invoice_prefix,
        last_number: parseInt(s.last_number, 10) || 0, default_concept: s.default_concept, zero_vat_note: s.zero_vat_note,
        payment_methods: s.payment_methods.filter((m) => m.name.trim() && m.text.trim()),
      };
      setS((await axios.put(`${API}/admin/billing/settings`, body)).data);
      toast.success('Datos de facturación guardados');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try { await axios.post(`${API}/admin/billing/logo`, fd); toast.success('Logo actualizado'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error al subir el logo'); }
  };
  const resetLogo = async () => { await axios.delete(`${API}/admin/billing/logo`); toast.success('Logo de KeyPubli restaurado'); load(); };
  const preview = async () => {
    const r = await axios.get(`${API}/admin/billing/preview-pdf`, { responseType: 'blob' });
    window.open(window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="billing-settings-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold">Datos de facturación</h2>
          <p className="text-sm text-muted-foreground">Lo que sale en el PDF de tus facturas de ingreso.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={preview} data-testid="billing-preview-btn"><Eye className="h-4 w-4 mr-1" />Ver factura de ejemplo</Button>
          <Button onClick={save} disabled={saving} data-testid="billing-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Guardar
          </Button>
        </div>
      </div>

      <Card className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div><Label>Razón social</Label><Input value={s.company_name} onChange={(e) => set('company_name', e.target.value)} data-testid="billing-company-input" /></div>
          <div><Label>CIF / NIF</Label><Input value={s.nif} onChange={(e) => set('nif', e.target.value)} data-testid="billing-nif-input" /></div>
          <div><Label>Dirección (una línea por fila)</Label><Textarea rows={3} value={s.address} onChange={(e) => set('address', e.target.value)} data-testid="billing-address-input" /></div>
        </div>
        <div className="space-y-3">
          <Label>Logo</Label>
          <div className="border rounded-lg p-3 bg-slate-50 flex items-center justify-center h-24">
            {(s.logo_data_url || s.default_logo_data_url) ? <img src={s.logo_data_url || s.default_logo_data_url} alt="Logo" className="max-h-20" data-testid="billing-logo-preview" /> : <p className="text-xs text-muted-foreground text-center">{DEFAULT_LOGO_HINT}</p>}
          </div>
          <div className="flex gap-2">
            <label className="inline-flex items-center gap-1 px-3 py-1.5 border rounded-md text-sm cursor-pointer hover:bg-slate-50" data-testid="billing-logo-upload">
              <Upload className="h-4 w-4" />Subir logo (PNG/JPG)
              <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
            </label>
            {s.has_custom_logo && <Button variant="ghost" size="sm" onClick={resetLogo} data-testid="billing-logo-reset"><RotateCcw className="h-4 w-4 mr-1" />Usar el de KeyPubli</Button>}
          </div>
        </div>
      </Card>

      <Card className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div><Label>Prefijo</Label><Input value={s.invoice_prefix} onChange={(e) => set('invoice_prefix', e.target.value)} data-testid="billing-prefix-input" /></div>
        <div>
          <Label>Última factura emitida en {s.current_year} (nº)</Label>
          <Input type="number" min="0" value={s.last_number} onChange={(e) => set('last_number', e.target.value)} data-testid="billing-last-number-input" />
        </div>
        <div>
          <Label>Próxima factura</Label>
          <p className="h-10 flex items-center font-bold text-primary text-lg" data-testid="billing-next-number">
            {s.invoice_prefix}{s.current_year}{(parseInt(s.last_number, 10) || 0) + 1}
          </p>
        </div>
        <p className="md:col-span-3 text-xs text-muted-foreground -mt-2">Formato: prefijo + año + número. Cada año nuevo empieza otra vez desde 1.</p>
        <div className="md:col-span-2"><Label>Concepto por defecto</Label><Input value={s.default_concept} onChange={(e) => set('default_concept', e.target.value)} data-testid="billing-concept-input" /></div>
        <div className="md:col-span-3"><Label>Nota en facturas con IVA 0% (clientes de fuera de España)</Label><Textarea rows={2} value={s.zero_vat_note} onChange={(e) => set('zero_vat_note', e.target.value)} data-testid="billing-zero-vat-note-input" /></div>
      </Card>

      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold">Métodos de pago</p>
            <p className="text-xs text-muted-foreground">Al hacer cada factura eliges con un clic cuáles salen en el PDF.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => set('payment_methods', [...s.payment_methods, { id: '', name: '', text: '', default: false }])} data-testid="billing-method-add">
            <Plus className="h-4 w-4 mr-1" />Añadir método
          </Button>
        </div>
        {s.payment_methods.map((m, i) => <MethodRow key={m.id || `new-${i}`} m={m} i={i} update={updateMethod} remove={removeMethod} />)}
      </Card>
    </div>
  );
};

export default BillingSettingsTab;
